import { produce } from 'immer';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getErrorMessage,
  hasValidationErrors,
  setValidationErrors,
  showSuccessToast,
} from '../../../../../../../common/global/helpers';
import Button from '../../../../../../../common/widgets/Button';
import Dialog from '../../../../../../../common/widgets/Dialog';
import Input from '../../../../../../../common/widgets/Input';
import {
  Notification,
  NotificationType,
} from '../../../../../../../common/widgets/Notification';
import { AddTransactionData } from '../../../../../models/BudgetTransactionModel';
import { ErrorState, ErrorType } from '../../../../../models/Errors';
import { resources } from '../../../../../resources/resources';
import { employeeAdminService } from '../../../../../services/employee-admin';

interface AddTransactionDialogProps {
  employeeId: number;
  close: () => void;
  triggerReload: () => void;
}

export default function AddTransactionDialog(
  props: Readonly<AddTransactionDialogProps>,
) {
  const { t: translate } = useTranslation();
  const { employeeId, close, triggerReload } = props;
  const [transaction, setTransaction] = useState<AddTransactionData>({
    ...({} as AddTransactionData),
    employeeId,
  });
  const [errors, setErrors] = useState<ErrorState>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleSaveTransaction = async () => {
    setIsLoading(true);
    const response = await employeeAdminService.addTransaction(transaction);
    if (!response.hasErrors) {
      triggerReload();
      close();
      showSuccessToast(
        translate(
          resources.admin.employees.details.general.successSaveTransaction,
        ),
      );
    } else if (response.errorType === ErrorType.ValidationError) {
      setValidationErrors(response, setErrors);
    }
    setIsLoading(false);
  };

  const clearErrors = (name: string) => {
    setErrors(
      produce(draft => {
        if (draft) {
          draft[name] = [];
        }
      }),
    );
  };

  return (
    <Dialog
      closeDialog={() => close()}
      showCloseIcon
      closeOnClickOutside
      className="dialog-s employee-new-transaction"
    >
      <div className="dialog-content">
        <div className="dialog-title">
          {translate(
            resources.admin.employees.details.trasactions.addDialog.title,
          )}
        </div>
        {hasValidationErrors(errors, 'null') && (
          <Notification
            type={NotificationType.Error}
            messages={errors.null.map(e => ({
              message: getErrorMessage(e, translate),
              onShowDetailsClick: undefined,
            }))}
          />
        )}
        <Input
          value={transaction.description}
          label={translate(
            resources.admin.employees.details.trasactions.addDialog.description,
          )}
          onChange={e =>
            setTransaction(
              produce(d => {
                d.description = e.value.toString();
              }),
            )
          }
          errors={errors.description}
          clearErrors={() => clearErrors('description')}
        />
        <Input
          type="number"
          value={transaction.amount}
          label={translate(
            resources.admin.employees.details.trasactions.addDialog.amount,
          )}
          onChange={e =>
            setTransaction(
              produce(d => {
                d.amount = Number(e.value);
              }),
            )
          }
          errors={errors.amount}
          clearErrors={() => clearErrors('amount')}
        />
      </div>
      <div className="dialog-actions horizontal">
        <Button
          className="btn btn-solid"
          loading={isLoading}
          onClick={handleSaveTransaction}
        >
          {translate(resources.admin.employees.details.general.save)}
        </Button>
        <button onClick={() => close()} className="btn btn-outline">
          {translate(resources.admin.employees.details.general.cancel)}
        </button>
      </div>
    </Dialog>
  );
}
